import chroma from 'chroma-js';
import { DefaultTheme, Mode, ThemeUtils } from 'styled-components';

const rem = function (base: number, ...pxs: number[]): string {
  return pxs.map((px) => `${px / base}rem`).join(' ');
};
const em = function (base: number, ...pxs: number[]): string {
  return pxs.map((px) => `${px / base}em`).join(' ');
};
const fromRem = function (base: number, rem: string | number): number {
  return parseFloat(rem as string) * base;
};

export const Theme = (function (mode: Mode): DefaultTheme {
  const base = 16;

  const theme: Omit<DefaultTheme, 'utils'> = {
    mode,
    base,
    borderRadius: rem(base, 16),
    colors: {
      palette: {
        default: { main: '#E6E6E6', textContrast: '#34435A' },
        primary: { main: '#89BE3B', textContrast: '#FFFFFF' },
        secondary: { main: '#34435A', textContrast: '#EBECF2' },
        warning: { main: '#F3B559', textContrast: '#4A2C00' },
        error: { main: '#CC3333', textContrast: '#FFF5F5' },
        success: { main: '#89BE3B', textContrast: '#003824' },
      },
      text: {
        main: '#1E232E',
        heading: '#1E232E',
        body: '#858585',
        label: '#878a92',
        helper: '#595959',
      },
      background: {
        default: '#FFFFFF',
        paper: '#F4F4F4',
      },
    },
    gradients: {
      default: 'radial-gradient(0% 0% at 100% 100%, #E5E5E5 0%, #595959 100%);',
      primary: 'radial-gradient(0% 0% at 100% 100%, #6CA11E 0%, #A2CF5F 100%);',
      secondary: 'radial-gradient(0% 0% at 100% 100%, #425573 0%, #252F40 100%);',
      warning: 'radial-gradient(0% 0% at 100% 100%, #F6CA88 0%, #EFA029 100%);',
      error: 'radial-gradient(0% 0% at 100% 100%, #D65C5C 0%, #B82E2E 100%);',
      success: 'radial-gradient(0% 0% at 100% 100%, #6CA11E 0%, #A2CF5F 100%);',
    },
    fontFamilies: {
      header: 'Montserrat, sans-serif',
      body: 'Montserrat, sans-serif',
      accent: 'monospace',
    },
    zIndex: {
      appBar: 100,
      drawer: 110,
      modal: 120,
      snackbar: 130,
      tooltip: 140,
    },
    grid: {
      gutter: 8,
      columns: 12,
    },
    breakpoints: {
      mobile: 400,
      tablet: 640,
      laptop: 960,
      desktop: 1200,
    },
  };

  const utils: ThemeUtils = {
    color: (variant, name, opacity = 1) => {
      const color = theme.colors.palette[variant]?.[name];
      return color && chroma(color).alpha(opacity).css();
    },
    text: (name, opacity = 1) => {
      const color = theme.colors.text[name];
      return color && chroma(color).alpha(opacity).css();
    },
    background: (name) => theme.colors.background[name],
    gradient: (name) => theme.gradients[name],
    font: (name) => theme.fontFamilies[name],
    rem: (...args) => rem(theme.base, ...args),
    em: (...args) => em(theme.base, ...args),
    fromRem: (rem) => fromRem(theme.base, rem),
    css: (condition, attributes) => (condition ? attributes : {}),
    breakpoint: (device) => window.innerWidth >= theme.breakpoints[device],
  };

  return { ...theme, utils };
})('light');
